import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardHeader,
  CardContent,
  FormHelperText,
} from '@material-ui/core';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, isValid } from 'date-fns';
import { useFormik } from 'formik';
import { useThunkDispatch, useTypedSelector } from 'hooks';
import { allZones } from 'redux/actions/zones.actions';
import { fetchOffices } from 'redux/actions/catalog.actions';
import { shouldReload } from 'utils/state.handlers';
import { RenderIf } from 'components/Tools/Utils';
import { getPrimaryStatusLabel, getSecondaryStatusLabel, getPrimaryStatusColor, getSecondaryStatusColor } from 'utils/statusTranslations';

import { ComisionistType } from 'types/api/responses';
import { CreateComisionistRequest } from 'types/api/responses';
import {
  comisionistDataInitialValues,
  comisionistDataValidationSchema,
} from '../utils/comisionist.validation';


export type ComisionistDataFormHandle = {
  validate: () => Promise<boolean>;
};

type Props = {
  initialValues?: CreateComisionistRequest;
  onValuesChange: (values: CreateComisionistRequest) => void;
  isSubmitting: boolean;
};


export const ComisionistDataForm = forwardRef<ComisionistDataFormHandle, Props>(
  function ComisionistDataForm({ initialValues, onValuesChange, isSubmitting }, ref) {
    const dispatch = useThunkDispatch();
    const zones = useTypedSelector((s) => s.zones);
    const branchOffices = useTypedSelector((s) => s.catalog.offices);

    const formik = useFormik<CreateComisionistRequest>({
      initialValues: initialValues || comisionistDataInitialValues,
      enableReinitialize: true,
      validationSchema: comisionistDataValidationSchema,
      onSubmit: () => undefined,
    });

    useEffect(() => {
      if (shouldReload(zones)) {
        dispatch(allZones());
      }
    }, [zones, dispatch]);

    useEffect(() => {
      const branchOfficeId = initialValues?.branchOfficeId;
      const zoneId = initialValues?.zoneId;

      if (branchOfficeId && (!zoneId || zoneId === 0)) {
        dispatch(fetchOffices({}));
      } else if (zoneId && zoneId > 0) {
        dispatch(fetchOffices({ ZoneId: zoneId }));
      }
    }, [initialValues?.branchOfficeId, initialValues?.zoneId, dispatch]);

    // Cuando se cargan las sucursales en modo edición, resolver la zona desde branchOfficeId
    useEffect(() => {
      const branchOfficeId = initialValues?.branchOfficeId;
      const zoneId = formik.values.zoneId;
      if (branchOfficeId && (!zoneId || zoneId === 0) && branchOffices.data.length > 0) {
        const office = branchOffices.data.find((o) => o.id === branchOfficeId);
        if (office) {
          formik.setFieldValue('zoneId', office.zone.id);
        }
      }
    }, [branchOffices.data, initialValues?.branchOfficeId]);


    useEffect(() => {
      onValuesChange(formik.values);
    }, [formik.values, onValuesChange]);

    const handleZoneChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      const zoneId = Number(event.target.value);
      formik.setFieldValue('zoneId', zoneId);
      formik.setFieldValue('branchOfficeId', '');
      formik.setFieldTouched('branchOfficeId', false, false);
      if (zoneId > 0) {
        dispatch(fetchOffices({ ZoneId: zoneId }));
      }
    };

    const handleDuiChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;
      const prevValue = formik.values.dui || '';
      const digits = rawValue.replace(/\D/g, '').slice(0, 9);
      const isDeleting = rawValue.length < prevValue.length;

      let masked: string;
      if (isDeleting) {
        masked = digits;
      } else {
        masked = digits.length > 8
          ? `${digits.slice(0, 8)}-${digits.slice(8)}`
          : digits.length === 8
          ? `${digits}-`
          : digits;
      }
      formik.setFieldValue('dui', masked);
    };

    const handleTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      const type = event.target.value as ComisionistType;
      formik.setFieldValue('type', type);

      if (type === 'Informal') {
        formik.setFieldValue('nrc', '');
        formik.setFieldValue('isLargeContributor', undefined);
      }

      formik.setFieldTouched('dui', false, false);
      formik.setFieldTouched('nrc', false, false);
      formik.setFieldTouched('isLargeContributor', false, false);
    };

    const handleLargeContributorChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      const value = event.target.value as string;
      formik.setFieldValue(
        'isLargeContributor',
        value === 'true' ? true : value === 'false' ? false : undefined,
      );
    };

    const officesInZone = branchOffices.data.filter(
      (office) => office.zone.id === formik.values.zoneId,
    );

    useImperativeHandle(ref, () => ({
      validate: async () => {
        const errors = await formik.validateForm();
        formik.setTouched({
          name: true,
          type: true,
          dui: true,
          nit: true,
          nrc: true,
          branchOfficeId: true,
          zoneId: true,
          email: true,
          phone: true,
          isLargeContributor: true,
        });
        return Object.keys(errors).length === 0;
      },
    }));

    const getFieldError = (field: keyof CreateComisionistRequest) =>
      formik.touched[field] && formik.errors[field] ? String(formik.errors[field]) : '';

    const isFormal = formik.values.type === 'Formal';

    return (
      <Box>
        <Card style={{ marginBottom: '16px' }}>
          <CardHeader title="Información General del Comisionista" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Nombre del Comisionista"
                  name="name"
                  value={formik.values.name}
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Tipo de Comisionista"
                  name="type"
                  value={formik.values.type || ''}
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <RenderIf condition={!isFormal}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="DUI"
                    name="dui"
                    value={formik.values.dui || ''}
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </RenderIf>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="NIT"
                  name="nit"
                  value={formik.values.nit}
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <RenderIf condition={isFormal}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="NRC"
                    name="nrc"
                    value={formik.values.nrc || ''}
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </RenderIf>

              <RenderIf condition={isFormal}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="DUI"
                    name="dui"
                    value={formik.values.dui || ''}
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </RenderIf>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Cuenta AS400"
                  name="clientAccount"
                  value={formik.values.clientAccount || ''}
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Zona"
                  name="zoneId"
                  value={
                    zones.zones.find((z) => Number(z.id) === formik.values.zoneId)?.name || ''
                  }
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <RenderIf condition={formik.values.zoneId > 0}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Sucursal"
                    name="branchOfficeId"
                    value={
                      branchOffices.data.find((o) => o.id === formik.values.branchOfficeId)?.name || ''
                    }
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </RenderIf>

              <RenderIf condition={isFormal}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Gran Contribuyente"
                    name="isLargeContributor"
                    value={
                      formik.values.isLargeContributor === true
                        ? 'Sí'
                        : formik.values.isLargeContributor === false
                        ? 'No'
                        : ''
                    }
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </RenderIf>

              {/* Fecha Alta AS400 */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Fecha Alta AS400"
                  name="dateDischarge"
                  value={
                    formik.values.dateDischarge && isValid(parseISO(formik.values.dateDischarge))
                      ? format(parseISO(formik.values.dateDischarge), 'dd/MM/yyyy')
                      : ''
                  }
                  variant="outlined"
                  size="small"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  },
);

